import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { getWindow } from '@shared/utils/window';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuthenticatedUser } from '../auth/apiHelpers';
import * as apiHelpers from '../auth/apiHelpers';
import { LANDING_PATH } from '../paths';
import { PLAID_LINK_TOKEN_KEY } from './PlaidContext';

export interface UserContextState {
  user?: AuthenticatedUser;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: Dispatch<SetStateAction<AuthenticatedUser | undefined>>;
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>;
  signOut: () => Promise<void>;
  clearUserState: () => void;
}

export const AUTH_QUERY_KEY = ['auth'];

export const UserContext = createContext<UserContextState | null>(null);
UserContext.displayName = 'User';

export const useUserContext = () => useDefinedContext(UserContext);

interface UserContextProviderProps {
  children: ReactNode;
}

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const [user, setUser] = useState<AuthenticatedUser>();
  // Manual override for sign-in / sign-out flows that finish before the auth
  // query refetches. Cleared whenever fresh data arrives so the server stays
  // the source of truth.
  const [authOverride, setAuthOverride] = useState<boolean | null>(null);

  const signOutMutation = useMutation({
    mutationFn: apiHelpers.signOut,
    onSuccess: () => {
      localStorage.removeItem(PLAID_LINK_TOKEN_KEY);
      getWindow().location.replace(LANDING_PATH);
    },
    throwOnError: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: apiHelpers.checkAuth,
    throwOnError: true,
  });

  const isAuthenticated = authOverride ?? !!data?.isAuthenticated;

  useEffect(() => {
    if (!data) return;
    setAuthOverride(null);
    setUser(data.isAuthenticated ? data.user : undefined);
  }, [data]);

  const setIsAuthenticated: Dispatch<SetStateAction<boolean>> = (action) => {
    setAuthOverride((prev) => {
      const current = prev ?? !!data?.isAuthenticated;
      return typeof action === 'function'
        ? (action as (prev: boolean) => boolean)(current)
        : action;
    });
  };

  const clearUserState = (): void => {
    setAuthOverride(false);
    setUser(undefined);
  };

  const value = useMemo<UserContextState>(
    () => ({
      user,
      setUser,
      isAuthenticated,
      isLoading,
      setIsAuthenticated,
      clearUserState,
      signOut: async () => signOutMutation.mutateAsync(),
    }),
    [user, isAuthenticated, isLoading],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
